import { useState, useEffect } from 'react'
import {
  getKnowledgeBase,
  deleteKnowledgeBaseEntry,
  createKnowledgeBaseEntry,
  updateKnowledgeBaseEntry,
  KnowledgeBaseEntry,
  KnowledgeBaseInsert,
} from '@/services/chatbot-config'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Plus, Pencil, Trash2, Search, Code } from 'lucide-react'
import { KnowledgeBaseDialog } from './KnowledgeBaseDialog'

export const KnowledgeBaseTab = () => {
  const [entries, setEntries] = useState<KnowledgeBaseEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedEntry, setSelectedEntry] = useState<KnowledgeBaseEntry | null>(
    null,
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { toast } = useToast()

  const fetchEntries = async () => {
    setLoading(true)
    const { data, error } = await getKnowledgeBase()
    if (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load knowledge base',
      })
    } else {
      setEntries(data || [])
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchEntries()
  }, [])

  const filteredEntries = entries.filter((entry) => {
    const pattern = entry.trigger_pattern || entry.keywords?.join(', ') || ''
    return (
      pattern.toLowerCase().includes(search.toLowerCase()) ||
      entry.response_text.toLowerCase().includes(search.toLowerCase())
    )
  })

  const handleCreateOrUpdate = async (values: KnowledgeBaseInsert) => {
    setIsSubmitting(true)
    try {
      if (selectedEntry) {
        await updateKnowledgeBaseEntry(selectedEntry.id, values)
        toast({ title: 'Updated successfully' })
      } else {
        await createKnowledgeBaseEntry(values)
        toast({ title: 'Created successfully' })
      }
      setIsDialogOpen(false)
      fetchEntries()
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Operation failed',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure?')) return
    await deleteKnowledgeBaseEntry(id)
    toast({ title: 'Deleted successfully' })
    setEntries(entries.filter((e) => e.id !== id))
  }

  const openNew = () => {
    setSelectedEntry(null)
    setIsDialogOpen(true)
  }

  const openEdit = (entry: KnowledgeBaseEntry) => {
    setSelectedEntry(entry)
    setIsDialogOpen(true)
  }

  if (loading) return <Loader2 className="h-8 w-8 animate-spin mx-auto mt-10" />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative max-w-sm w-full">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search triggers..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Button onClick={openNew}>
          <Plus className="mr-2 h-4 w-4" /> Add Entry
        </Button>
      </div>

      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Trigger Pattern</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Response</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEntries.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center py-8 text-muted-foreground"
                >
                  No entries found.
                </TableCell>
              </TableRow>
            ) : (
              filteredEntries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="max-w-[200px] font-medium">
                    {entry.trigger_pattern || entry.keywords?.join(', ')}
                  </TableCell>
                  <TableCell>
                    {entry.is_regex ? (
                      <Badge
                        variant="outline"
                        className="text-purple-600 border-purple-200 bg-purple-50"
                      >
                        <Code className="w-3 h-3 mr-1" /> Regex
                      </Badge>
                    ) : (
                      <Badge variant="secondary">Keywords</Badge>
                    )}
                  </TableCell>
                  <TableCell
                    className="max-w-[400px] truncate"
                    title={entry.response_text}
                  >
                    {entry.response_text}
                  </TableCell>
                  <TableCell>
                    <Badge variant={entry.is_active ? 'default' : 'outline'}>
                      {entry.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(entry)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:text-red-600"
                      onClick={() => handleDelete(entry.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <KnowledgeBaseDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        initialData={selectedEntry}
        onSubmit={handleCreateOrUpdate}
        isSubmitting={isSubmitting}
      />
    </div>
  )
}
